<template>
  <div class="calculations-results" data-testid="calculations-results-view">
    <div class="results-header">
      <div class="success-icon">
        <i class="pi pi-check-circle" />
      </div>
      <h3>{{ t('Calculos Concluidos') }}</h3>
      <p class="results-description">{{ t('Todos os calculos foram processados com sucesso') }}</p>
    </div>

    <!-- Summary Cards -->
    <div class="results-cards">
      <box v-if="results.executed !== null" type="primary" class="result-card">
        <div class="card-content">
          <span class="card-label">{{ t('Comissão Executado') }}</span>
          <span class="card-value text-green-600">{{ n(results.executed || 0, 'currency') }}</span>
        </div>
      </box>

      <box v-if="results.soldRemotely !== null" type="primary" class="result-card">
        <div class="card-content">
          <span class="card-label">{{ t('Comissão Vendido') }}</span>
          <span class="card-value text-green-600">{{ n(results.soldRemotely || 0, 'currency') }}</span>
        </div>
      </box>

      <box v-if="results.fixed !== null" type="primary" class="result-card">
        <div class="card-content">
          <span class="card-label">{{ t('Valores Fixos') }}</span>
          <span class="card-value text-blue-600">{{ n(results.fixed || 0, 'currency') }}</span>
        </div>
      </box>
    </div>

    <!-- Grand Total -->
    <box type="primary" class="total-card">
      <div class="total-content">
        <span class="total-label">{{ t('Total Geral') }}</span>
        <span class="total-value">{{ n(grandTotal, 'currency') }}</span>
      </div>
    </box>

    <!-- User Breakdown (if available) -->
    <div v-if="userBreakdown && userBreakdown.length > 0" class="user-breakdown">
      <h4>{{ t('Por Funcionario') }}</h4>
      <DataTable
        :value="userBreakdown"
        :paginator="false"
        responsiveLayout="scroll"
        class="breakdown-table"
      >
        <Column field="user_name" :header="t('Funcionario')">
          <template #body="{ data }">
            {{ data.user_name || t('Funcionario') }}
          </template>
        </Column>
        <Column field="gross_total" :header="t('Valor Total')">
          <template #body="{ data }">
            <strong class="text-primary">{{ n(parseFloat(data.gross_total) || 0, 'currency') }}</strong>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Action Buttons -->
    <div class="results-actions">
      <Button
        :label="t('Fechar')"
        icon="pi pi-check"
        class="p-button-success"
        data-testid="calculations-results-close-button"
        @click="$emit('close')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface CalculationResults {
  executed: number | null;
  soldRemotely: number | null;
  fixed: number | null;
}

interface UserBreakdownItem {
  user_name?: string;
  gross_total?: number | string;
  [key: string]: unknown;
}

const { t, n } = useI18n();

const props = defineProps<{
  results: CalculationResults;
  userBreakdown?: UserBreakdownItem[];
}>();

defineEmits<{
  close: [];
}>();

const grandTotal = computed((): number => {
  const executed = Number(props.results.executed) || 0;
  const soldRemotely = Number(props.results.soldRemotely) || 0;
  const fixed = Number(props.results.fixed) || 0;
  return executed + soldRemotely + fixed;
});
</script>

<style scoped>
.calculations-results {
  padding: 1.5rem;
}

.results-header {
  text-align: center;
  margin-bottom: 2rem;
}

.success-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.success-icon i {
  font-size: 3rem;
  color: var(--green-500);
}

.results-header h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: var(--text-color);
}

.results-description {
  margin: 0;
  color: var(--text-color-secondary);
}

.results-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.result-card {
  background-color: var(--surface-50);
}

.card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.card-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.card-value {
  font-size: 1.25rem;
  font-weight: 600;
}

.total-card {
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
  border: 2px solid var(--primary-200);
  margin-bottom: 1.5rem;
}

.total-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.total-label {
  font-size: 1rem;
  font-weight: 500;
  color: var(--primary-700);
}

.total-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-600);
}

.user-breakdown {
  margin-bottom: 1.5rem;
}

.user-breakdown h4 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  color: var(--text-color);
}

.breakdown-table {
  font-size: 0.875rem;
}

.results-actions {
  display: flex;
  justify-content: center;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-border);
}

@media (max-width: 768px) {
  .results-cards {
    grid-template-columns: 1fr;
  }
}
</style>
